import { type IntelOrgMemberDetailDto, IntelligencePersona } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { Badge } from '@components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import {
  INTELLIGENCE_PERSONA_OPTIONS,
  getIntelligencePersonaBadgeVariant,
  getIntelligencePersonaLabel,
} from '@utils/intelligence-persona.util';
import { Trash2Icon } from 'lucide-react';
import type { FC } from 'react';

type IntelTeamMemberRowProps = {
  isProgramAdmin: boolean;
  isUpdating?: boolean;
  member: IntelOrgMemberDetailDto;
  //
  onPersonaChange: (persona: IntelligencePersona) => void;
  onRemove: () => void;
};

export const IntelTeamMemberRow: FC<IntelTeamMemberRowProps> = (props) => {
  const { isProgramAdmin, isUpdating = false, member, onPersonaChange, onRemove } = props;

  const name = member.fullName || member.email;

  const handlePersonaChange = (value: string): void => {
    const nextPersona = INTELLIGENCE_PERSONA_OPTIONS.find((option) => option.value === value)?.value;
    if (nextPersona) {
      onPersonaChange(nextPersona);
    }
  };

  return (
    <div className="flex w-full items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4">
      <AvatarItem
        altText={`${name} avatar`}
        description={member.email}
        imageUrl={member.avatarUrl ?? undefined}
        initialText={name}
        title={name}
      />

      {isProgramAdmin ? (
        <div className="flex shrink-0 items-center gap-2">
          <div className="w-44">
            <Select
              disabled={isUpdating}
              onValueChange={handlePersonaChange}
              value={member.persona ?? undefined}
            >
              <SelectTrigger className="h-10 border border-solid border-gray-200 bg-gray-100 p-3">
                <SelectValue placeholder="Select persona" />
              </SelectTrigger>
              <SelectContent>
                {INTELLIGENCE_PERSONA_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <IconButton
            ariaLabel={`Remove ${name}`}
            disabled={isUpdating}
            icon={<Trash2Icon />}
            onClick={onRemove}
            title="Remove member"
            variant="destructive"
          />
        </div>
      ) : (
        <Badge
          className="shrink-0"
          variant={getIntelligencePersonaBadgeVariant(member.persona)}
        >
          {getIntelligencePersonaLabel(member.persona)}
        </Badge>
      )}
    </div>
  );
};
